import { useEffect } from 'react';

const SCROLL_ROOT_SELECTOR = '[data-transcript-scroll-root]';

export const resolveTranscriptScrollRootEl = (): HTMLElement | null =>
  document.querySelector(SCROLL_ROOT_SELECTOR) as HTMLElement | null;

export type TranscriptInteractionLockInput = {
  isEditMode: boolean;
  allowEditorMount: boolean;
  editorReady: boolean;
  chunkLoading: boolean;
  scrollFrozen: boolean;
  stressScrollPending?: boolean;
  scrollLoading?: boolean;
  viewportNeedsCatchUp?: boolean;
  seekLoadingUi?: boolean;
  hasPlaybackSeekTarget?: boolean;
  /** Legacy SPP-655 shell — scroll/seek overlay active. */
  showLoadingOverlay?: boolean;
};

/** Shared gate — transcript must finish loading before any user interaction. */
const resolveTranscriptChunkTransitionLocked = (
  input: TranscriptInteractionLockInput,
): boolean =>
  !input.allowEditorMount ||
  !input.editorReady ||
  (!input.editorReady && input.chunkLoading) ||
  (!input.editorReady && input.scrollFrozen) ||
  (!input.editorReady && !!input.stressScrollPending) ||
  !!input.viewportNeedsCatchUp ||
  !!input.showLoadingOverlay;

/**
 * Block Slate edits and caption clicks while chunk/window prep is in flight.
 */
export const resolveTranscriptInteractionLocked = (
  input: TranscriptInteractionLockInput,
): boolean => {
  if (input.isEditMode) {
    return resolveTranscriptChunkTransitionLocked(input);
  }
  return (
    resolveTranscriptChunkTransitionLocked(input) ||
    (!input.editorReady && !!input.seekLoadingUi) ||
    (!input.editorReady && !!input.hasPlaybackSeekTarget)
  );
};

/** Domain wait lock — single formula for shell chrome + store (Track B). */
export const resolveDwcmTranscriptWaitLocked = (
  input: TranscriptInteractionLockInput & {
    wheelBlockActive: boolean;
    atBoundaryTrigger: boolean;
  },
): boolean =>
  resolveTranscriptInteractionLocked(input) ||
  (!input.editorReady && input.wheelBlockActive) ||
  (!input.editorReady && input.scrollFrozen) ||
  (input.atBoundaryTrigger &&
    !!input.viewportNeedsCatchUp &&
    (!!input.scrollLoading ||
      !!input.stressScrollPending ||
      !input.editorReady));

/** Injected once — wait cursor while trigger load or wheel block is active. */
const TRANSCRIPT_LOADING_CURSOR_STYLE_ID =
  'sonnant-transcript-chunk-loading-cursor';

export const ensureTranscriptLoadingCursorStyle = () => {
  if (document.getElementById(TRANSCRIPT_LOADING_CURSOR_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = TRANSCRIPT_LOADING_CURSOR_STYLE_ID;
  style.textContent = `
    [data-transcript-wait-cursor="1"],
    [data-transcript-wait-cursor="1"] *,
    [data-transcript-wheel-locked="1"],
    [data-transcript-wheel-locked="1"] * {
      cursor: wait !important;
    }
  `;
  document.head.appendChild(style);
};

/** Mark scroll root busy while wheel/touch scroll is blocked at a chunk seam. */
export const syncTranscriptWheelLockCursor = (
  root: HTMLElement,
  wheelLocked: boolean,
): void => {
  ensureTranscriptLoadingCursorStyle();
  if (wheelLocked) {
    root.setAttribute('data-transcript-wheel-locked', '1');
    root.setAttribute('aria-busy', 'true');
    return;
  }
  root.removeAttribute('data-transcript-wheel-locked');
  if (!root.hasAttribute('data-transcript-chunk-loading')) {
    root.removeAttribute('aria-busy');
  }
};

/** Loading overlay active while transcript prep is in flight. */
export const resolveTranscriptLoadingCue = (
  interactionLocked: boolean,
  loadingCueActive: boolean,
): boolean => interactionLocked || loadingCueActive;

/**
 * SPP-675-enhancement — when to show the full-viewport processing overlay (S2/S3 + gap fill).
 * Localized seam/spacer/sticky chrome owns S1 / boundary expansion; overlay fills silent gaps.
 */
export type ResolveDwcmViewportOverlayInput = {
  inPostSeekGrace: boolean;
  editorEverReady: boolean;
  chunkScrollHardLocked: boolean;
  loadingZoneInteractionBlocked: boolean;
  stressScrollBusy: boolean;
  seekBusy: boolean;
  scrollLoading: boolean;
  editorReady: boolean;
  boundaryChunkLoading: boolean;
  transcriptWaitLocked: boolean;
  hasBoundarySkeletonCue: boolean;
  farChunkViewportPending: boolean;
};

export type ResolvePlaybackSeekReadyToFinalizeInput = {
  editorReady: boolean;
  sliding?: boolean;
  target: { chunkId: string; time: number } | null;
  window: { startOrdinal: number; endOrdinal: number } | null;
  targetOrdinal: number | null;
};

/**
 * Far-chunk progress-bar seek must not finalize while the old window is still
 * the ready editor (that drops overlay before remount and re-arms the seek).
 */
export const resolvePlaybackSeekReadyToFinalize = (
  input: ResolvePlaybackSeekReadyToFinalizeInput,
): boolean => {
  if (input.sliding) return false;
  if (!input.editorReady) return false;
  if (!input.target) return true;
  if (input.targetOrdinal == null || !input.window) return false;
  return (
    input.targetOrdinal >= input.window.startOrdinal &&
    input.targetOrdinal <= input.window.endOrdinal
  );
};

export type PendingViewportCatchUpDecision =
  | 'accept'
  | 'defer-seek'
  | 'supersede'
  | 'drop-pending-accept'
  | 'drop-pending-reject';

export type ResolvePendingViewportCatchUpOnEditorReadyInput = {
  pendingCatchUpWindow: { startOrdinal: number; endOrdinal: number } | null;
  mountedWindow: { startOrdinal: number; endOrdinal: number };
  seekInProgress: boolean;
  findJumpInProgress: boolean;
  findTargetOrdinal: number | null;
};

/**
 * S5: a newer scroll catch-up may steal a stale mount — except during find jump.
 * Stale spacer scrollTop maps to the last chunk; applying it leaves the find
 * window never-ready and the skeleton up forever.
 */
export const resolvePendingViewportCatchUpOnEditorReady = (
  input: ResolvePendingViewportCatchUpOnEditorReadyInput,
): PendingViewportCatchUpDecision => {
  if (!input.pendingCatchUpWindow) return 'accept';

  if (input.findJumpInProgress) {
    const targetInMounted =
      input.findTargetOrdinal != null &&
      input.findTargetOrdinal >= input.mountedWindow.startOrdinal &&
      input.findTargetOrdinal <= input.mountedWindow.endOrdinal;
    return targetInMounted ? 'drop-pending-accept' : 'drop-pending-reject';
  }

  if (input.seekInProgress) return 'defer-seek';

  if (
    input.pendingCatchUpWindow.startOrdinal !==
      input.mountedWindow.startOrdinal ||
    input.pendingCatchUpWindow.endOrdinal !== input.mountedWindow.endOrdinal
  ) {
    return 'supersede';
  }
  return 'accept';
};

export const resolveShowViewportProcessingOverlay = (
  input: ResolveDwcmViewportOverlayInput,
): boolean => {
  // Progress-bar seek: full overlay + interaction lock until the window is ready.
  // Post-seek grace must not hide that chrome while a seek remount is in flight.
  if (input.seekBusy) {
    return true;
  }
  if (input.inPostSeekGrace) return false;
  if (input.chunkScrollHardLocked || input.loadingZoneInteractionBlocked) {
    return true;
  }
  if (!input.editorEverReady) return false;

  // S2 remount always owns the full-viewport overlay (even at a seam).
  if (input.stressScrollBusy) {
    return true;
  }
  // Boundary expansion keeps localized seam/spacer chrome; otherwise block the pane.
  if (input.scrollLoading && !input.editorReady && !input.boundaryChunkLoading) {
    return true;
  }

  // Locked for next-segment load with no other primary chrome visible
  // (includes stale painted window still wait-locked).
  if (
    input.transcriptWaitLocked &&
    !input.hasBoundarySkeletonCue &&
    !input.farChunkViewportPending
  ) {
    return true;
  }

  return false;
};

export type TranscriptChunkLoadingCueOptions = {
  /** When true, overlay blocks scroll-pane clicks and wheel (paired with shell wheel lock). */
  blockInteraction?: boolean;
  /** @deprecated Viewport overlay is rendered via TranscriptViewportLoadingOverlay. */
  viewportOverlay?: boolean;
  /** Wait cursor on the transcript scroll pane (e.g. boundary trigger until editor ready). */
  waitCursor?: boolean;
};

/**
 * aria-busy on the transcript scroll pane while chunk/window loads.
 * Visual overlay: TranscriptViewportLoadingOverlay in the editor shell.
 */
export const useTranscriptChunkLoadingCue = (
  loading: boolean,
  options?: TranscriptChunkLoadingCueOptions,
): void => {
  const waitCursor = !!options?.waitCursor;

  useEffect(() => {
    const root = resolveTranscriptScrollRootEl();
    if (!root) return;

    ensureTranscriptLoadingCursorStyle();

    if (waitCursor) {
      root.setAttribute('data-transcript-wait-cursor', '1');
    } else {
      root.removeAttribute('data-transcript-wait-cursor');
    }

    if (!loading) {
      root.removeAttribute('data-transcript-chunk-loading');
      if (!root.hasAttribute('data-transcript-wheel-locked')) {
        root.removeAttribute('aria-busy');
      }
      return () => {
        root.removeAttribute('data-transcript-wait-cursor');
      };
    }

    root.setAttribute('data-transcript-chunk-loading', '1');
    root.setAttribute('aria-busy', 'true');

    return () => {
      root.removeAttribute('data-transcript-chunk-loading');
      root.removeAttribute('data-transcript-wait-cursor');
      if (!root.hasAttribute('data-transcript-wheel-locked')) {
        root.removeAttribute('aria-busy');
      }
    };
  }, [loading, waitCursor]);
};
